import {
  Collapse,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {isLoaded} from 'Core/models/Async';
import {QuotingRouteConstants} from 'Quoting/Routes';
import {
  clearSelectedQuotesForAction,
  generateProposal,
  retrieveQuotesForGenerateProposal,
} from 'Quoting/ducks/quoteActions';
import {ProposalQuoteDetails, ProposalResponseDTO} from 'Quoting/models/DTOs/Proposal';
import {A360ProductModel} from 'Quoting/models/Quoting';
import {QuoteStatus} from 'Quoting/models/enum';
import {getFormattedDate} from 'Quoting/utils/dateUtils';
import {getProductNameFromModel, quoteStatusLabelMapping} from 'Quoting/utils/helpers';
import {safeRenderValue} from 'Quoting/utils/utilities';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import {NoOp} from 'Shared/utilities/IOUtilities';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useParams} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import {CardAction, QuotingCard} from '../shared/QuotingCard/QuotingCard';

type GenerateProposalParams = {
  opportunityNumber: string;
  customerNumber: string;
  proposalRootId: string;
  proposalRevisionNo: string;
};

const useGenerateProposalStyles = makeStyles(() => ({
  capitalizeProposalButton: {
    textTransform: 'capitalize',
  },
}));

export const GenerateProposalPage = () => {
  const {proposalToGenerate} = useSelector((state: ReducerState) => state.quoteActions);
  const history = useHistory();
  const dispatch = useDispatch();
  const classes = useGenerateProposalStyles();

  useEffect(() => {
    if (isLoaded(proposalToGenerate)) {
      const quoteLinks = proposalToGenerate.Data.quotes.map(q => q._uri);
      dispatch(retrieveQuotesForGenerateProposal(quoteLinks));
    }
    return () => {
      dispatch(clearSelectedQuotesForAction());
    };
  }, [proposalToGenerate]);

  const {opportunityNumber, proposalRootId, proposalRevisionNo} = useParams<GenerateProposalParams>();

  const handleGenerateProposal = useCallback(() => {
    if (!Number.isNaN(Number(proposalRevisionNo))) {
      dispatch(generateProposal(proposalRootId, Number(proposalRevisionNo), opportunityNumber));
    }
  }, [generateProposal, proposalRootId, proposalRevisionNo]);

  const handleSaveOrCancel = useCallback(
    () => history.push(QuotingRouteConstants.OPPORTUNITY_SUMMARY(opportunityNumber ?? '')),
    [opportunityNumber]
  );

  const headerActions: CardAction[] = useMemo(
    () => [
      {
        label: 'Cancel',
        handlerFn: handleSaveOrCancel,
        props: {
          className: classes.capitalizeProposalButton,
          ariaLabel: 'Cancel',
        },
      },
      {
        label: 'Save & Exit',
        handlerFn: handleSaveOrCancel,
        props: {
          className: classes.capitalizeProposalButton,
          ariaLabel: 'Save & Exit',
        },
      },
      {
        label: 'Save',
        handlerFn: NoOp,
        props: {
          variant: 'outlined',
          className: classes.capitalizeProposalButton,
          ariaLabel: 'Save',
        },
      },
      {
        label: 'Generate Proposal',
        handlerFn: handleGenerateProposal,
        props: {
          variant: 'contained',
          color: 'primary',
          className: classes.capitalizeProposalButton,
          ariaLabel: 'Generate Proposal',
        },
      },
    ],
    [handleSaveOrCancel, handleSaveOrCancel, handleGenerateProposal]
  );

  return (
    <>
      <QuotingCard title={'Opportunity Case'} subtitle={`#${opportunityNumber}`} headerActions={headerActions} />
      {proposalToGenerate &&
        renderAsyncView(proposalToGenerate, proposalDetails =>
          proposalDetails ? (
            <ProposalDetails key={proposalDetails.number} proposal={proposalDetails} />
          ) : (
            <div>No Data</div>
          )
        )}
    </>
  );
};

const ProposalDetails = ({proposal}: {proposal: ProposalResponseDTO}) => {
  const {quotesForSelectedProposal} = useSelector((state: ReducerState) => state.quoteActions);
  return (
    <div>
      <div style={{padding: '0 2rem'}}>
        <Typography component="h2" style={{fontSize: '1.5rem', fontWeight: 600}}>
          Proposal
        </Typography>
        <Typography style={{fontSize: '1.125rem'}}>{proposal.number}</Typography>
        <Typography style={{fontSize: '1.125rem'}}>{proposal.name}</Typography>
      </div>
      <QuotingCard title={<ProposalDetailTitle />} titleAsHeader>
        <List>
          {Array.isArray(quotesForSelectedProposal) &&
            quotesForSelectedProposal.length > 0 &&
            quotesForSelectedProposal.map((quote: ProposalQuoteDetails) => (
              <>
                <ProposalProductDetails key={quote.master?._key?.rootId} quote={quote} />
                <Divider />
              </>
            ))}
        </List>
      </QuotingCard>
    </div>
  );
};

const ProposalProductDetails = ({quote}: {quote: ProposalQuoteDetails}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <ProposalQuoteDetailHeader
        onExpand={() => setExpanded(prevState => !prevState)}
        expanded={expanded}
        onDeleteQuoteFromProposal={NoOp}
        quote={quote}
      />
      <Collapse in={expanded} unmountOnExit>
        <ProposalQuoteDetail expanded={expanded} quote={quote} />
      </Collapse>
    </>
  );
};

const ProposalDetailTitle = () => {
  const columnHeaders = ['Quote', 'Product', 'Status', 'Effective Date', ''];
  return (
    <Grid container spacing={6}>
      <Grid item key={columnHeaders[0]} xs={4}>
        {columnHeaders[0]}
      </Grid>
      {columnHeaders.slice(1).map(header => (
        <Grid item key={header} xs={2}>
          {header}
        </Grid>
      ))}
    </Grid>
  );
};

const useProposalDetailsStyles = makeStyles(() => ({
  expand: {
    transform: 'rotate(0deg)',
  },
  expandOpen: {
    transform: 'rotate(90deg)',
  },
  proposalName: {
    display: 'flex',
    alignItems: 'center',
  },
}));

type ProposalQuoteDetailHeaderProps = {
  expanded: boolean;
  onExpand: () => void;
  onDeleteQuoteFromProposal: () => void;
  quote: ProposalQuoteDetails;
};

const ProposalQuoteDetailHeader = ({
  expanded,
  onExpand,
  quote,
  onDeleteQuoteFromProposal,
}: ProposalQuoteDetailHeaderProps) => {
  const classes = useProposalDetailsStyles();

  return (
    <ListItem>
      <Grid container className={classes.proposalName} spacing={6}>
        <Grid item className={classes.proposalName} xs={4}>
          <ListItemIcon>
            {/* <IconButton
              onClick={onExpand}
              className={classNames(classes.expand, {[classes.expandOpen]: expanded})}
              size="small"
            >
              <ChevronRight /> */}
            {/* </IconButton> */}
          </ListItemIcon>
          <ListItemText primary={quote.master?.policyNumber} />
        </Grid>
        <Grid item xs={2}>
          <ListItemText primary={getProductNameFromModel(quote.master?._modelName as A360ProductModel)} />
        </Grid>
        <Grid item xs={2}>
          <ListItemText primary={safeRenderValue(quoteStatusLabelMapping[quote.master?.state as QuoteStatus])} />
        </Grid>
        <Grid item xs={2}>
          <ListItemText primary={getFormattedDate(quote.master?.transactionDetails?.txEffectiveDate)} />
        </Grid>
        <Grid item xs={2}>
          <ListItemSecondaryAction>
            {/* <IconButton onClick={onDeleteQuoteFromProposal} edge="end" aria-label="delete" size="small">
              <Delete style={{color: '#101837'}} />
            </IconButton> */}
          </ListItemSecondaryAction>
        </Grid>
      </Grid>
    </ListItem>
  );
};

const ProposalQuoteDetail = (_props: any) => {
  return <div style={{padding: '1rem 4rem'}}>Quote Rating Details</div>;
};
